import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Download, 
  FileText,
  BarChart3,
  MessageSquare,
  Clock
} from 'lucide-react';
import { RevisionSession } from '@/types/resumeRevision';

interface Step5AccuracyReportProps {
  session: RevisionSession;
  onComplete?: (session: RevisionSession) => void;
}

interface AccuracyMetrics {
  totalChanges: number;
  exactMatches: number;
  partialMatches: number;
  notPossible: number;
  overallScore: number;
}

interface ChangeLog {
  id: string;
  instruction: string;
  status: 'exact' | 'partial' | 'not_possible';
  chosenOption: string;
  alternativeOptions: string[];
  userTweaks?: string;
  aiReasoning: string;
  timestamp: string;
}

export const Step5AccuracyReport: React.FC<Step5AccuracyReportProps> = ({
  session,
  onComplete
}) => {
  const [accuracyMetrics, setAccuracyMetrics] = useState<AccuracyMetrics | null>(null);
  const [changeLog, setChangeLog] = useState<ChangeLog[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(true);

  useEffect(() => {
    generateAccuracyReport();
  }, []);

  const generateAccuracyReport = async () => {
    setIsGeneratingReport(true);
    
    try {
      // TODO: Generate actual accuracy report from session data
      // For now, mock the report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockMetrics: AccuracyMetrics = {
        totalChanges: 5,
        exactMatches: 3,
        partialMatches: 2,
        notPossible: 0,
        overallScore: 85
      };

      const mockChangeLog: ChangeLog[] = [
        {
          id: '1',
          instruction: 'Add Python and React to the Skills section',
          status: 'exact',
          chosenOption: 'JavaScript, HTML, CSS, Node.js, Python, React',
          alternativeOptions: [
            'Frontend: JavaScript, HTML, CSS, React | Backend: Node.js, Python',
            'JavaScript, HTML, CSS, Node.js, Python (Django/Flask), React'
          ],
          aiReasoning: 'Successfully added both technologies while maintaining the existing format and flow of the skills section.',
          timestamp: new Date().toISOString()
        },
        {
          id: '2', 
          instruction: 'Quantify achievements in the Experience section',
          status: 'partial',
          chosenOption: 'Improved application performance by 40% through code optimization',
          alternativeOptions: [
            'Enhanced application performance through systematic code optimization',
            'Led performance optimization initiatives resulting in measurable improvements'
          ],
          userTweaks: 'Changed "by 40%" to "by over 35%"',
          aiReasoning: 'Added specific metrics where possible, but some achievements needed user input for accurate quantification.',
          timestamp: new Date().toISOString()
        }
      ];

      setAccuracyMetrics(mockMetrics);
      setChangeLog(mockChangeLog);
    } catch (error) {
      console.error('Error generating accuracy report:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'exact':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'partial':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'not_possible':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'exact':
        return <CheckCircle className="h-4 w-4" />;
      case 'partial':
        return <AlertTriangle className="h-4 w-4" />;
      case 'not_possible':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (isGeneratingReport) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-48">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-sm text-muted-foreground">
                Generating accuracy report and comparing results...
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                This may take a few moments
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Report Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Revision Accuracy Report
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Final analysis of requested changes vs. actual results with detailed breakdown.
          </p>
        </CardHeader>
      </Card>

      {/* Accuracy Metrics */}
      {accuracyMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {accuracyMetrics.exactMatches}
                </div>
                <div className="text-sm text-muted-foreground">Exact Matches</div>
                <Progress 
                  value={(accuracyMetrics.exactMatches / accuracyMetrics.totalChanges) * 100} 
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {accuracyMetrics.partialMatches}
                </div>
                <div className="text-sm text-muted-foreground">Partial Matches</div>
                <Progress 
                  value={(accuracyMetrics.partialMatches / accuracyMetrics.totalChanges) * 100} 
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {accuracyMetrics.notPossible}
                </div>
                <div className="text-sm text-muted-foreground">Not Possible</div>
                <Progress 
                  value={(accuracyMetrics.notPossible / accuracyMetrics.totalChanges) * 100} 
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {accuracyMetrics.overallScore}%
                </div>
                <div className="text-sm text-muted-foreground">Overall Score</div>
                <Progress 
                  value={accuracyMetrics.overallScore} 
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detailed Report */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Detailed Change Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="options">Options Review</TabsTrigger>
              <TabsTrigger value="log">Full Log</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-4">
              <div className="space-y-4">
                {changeLog.map((change) => (
                  <div key={change.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-sm">{change.instruction}</h4>
                      <Badge 
                        variant="outline" 
                        className={`${getStatusColor(change.status)} flex items-center gap-1`}
                      >
                        {getStatusIcon(change.status)}
                        {change.status === 'exact' ? 'Exact' : 
                         change.status === 'partial' ? 'Partial' : 'Not Possible'}
                      </Badge>
                    </div>
                    
                    <div className="text-sm space-y-2">
                      <div>
                        <span className="font-medium">Result:</span>
                        <p className="text-muted-foreground italic">"{change.chosenOption}"</p>
                      </div>
                      
                      {change.userTweaks && (
                        <div>
                          <span className="font-medium">User Tweaks:</span>
                          <p className="text-blue-600 text-xs">{change.userTweaks}</p>
                        </div>
                      )}
                      
                      <div>
                        <span className="font-medium">AI Analysis:</span>
                        <p className="text-muted-foreground text-xs">{change.aiReasoning}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="options" className="space-y-4">
              <div className="space-y-4">
                {changeLog.map((change) => (
                  <div key={change.id} className="border rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-3">{change.instruction}</h4>
                    
                    <div className="space-y-2">
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-900">Chosen Option</span>
                        </div>
                        <p className="text-sm text-green-800">"{change.chosenOption}"</p>
                      </div>
                      
                      {change.alternativeOptions.map((option, index) => (
                        <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">
                              Alternative {index + 1}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">"{option}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="log" className="space-y-4">
              <div className="space-y-3">
                {changeLog.map((change) => (
                  <div key={change.id} className="text-sm border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{change.instruction}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(change.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p><strong>Status:</strong> {change.status}</p>
                      <p><strong>Result:</strong> "{change.chosenOption}"</p>
                      <p><strong>Options Generated:</strong> {change.alternativeOptions.length + 1}</p>
                      {change.userTweaks && <p><strong>User Modifications:</strong> {change.userTweaks}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Final Downloads & Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Final Outputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full justify-start" size="lg">
              <Download className="h-4 w-4 mr-2" />
              Download Clean Resume
            </Button>
            
            <Button variant="outline" className="w-full justify-start" size="lg">
              <FileText className="h-4 w-4 mr-2" />
              Download with Changes Highlighted
            </Button>

            <div className="pt-2 border-t text-xs text-muted-foreground">
              Both versions available in PDF, DOCX, and HTML formats
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Session Complete</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span>Session Duration:</span>
                <span>12 minutes</span>
              </div>
              <div className="flex justify-between">
                <span>Changes Applied:</span>
                <span>{accuracyMetrics?.totalChanges || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Success Rate:</span>
                <span>{accuracyMetrics?.overallScore || 0}%</span>
              </div>
            </div>

            <div className="pt-2 border-t">
              <Button 
                className="w-full"
                onClick={() => onComplete && onComplete(session)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Session
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Questions & Feedback */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-4">
              Have questions about the changes or need additional revisions?
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm">
                Start New Session
              </Button>
              <Button variant="outline" size="sm">
                Provide Feedback
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};